/**
 * Cross-form consistency engine — detects "you said X here but Y over there"
 * contradictions across the 526EZ, 21-0781, Claim Tracker workspace, and
 * statements.
 *
 * Pure functions only. Returns the same finding shape as `formAudit.js` so
 * the existing `FormAuditPanel` can render the output without modification:
 *   { id, severity, title, why, fix }
 *
 * Severity ladder identical to formAudit:
 *   • critical → "Missing Critical Info"        (blocks submission)
 *   • confusion → "Possible Claim Confusion"    (wrong field, wrong place)
 *   • review → "Needs Review"                   (ambiguous, may be ok)
 *   • ready → "Ready"                           (passed check)
 *
 * Tested in __tests__/crossFormConsistency.test.js. No UI dependencies.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

// DSM-style mental-health condition keywords. Used to detect when a veteran
// claims a mental condition on the 526EZ but never opens the 21-0781
// worksheet (or vice versa).
const MENTAL_KEYWORDS = [
  "ptsd", "post-traumatic", "post traumatic",
  "anxiety", "generalized anxiety", "gad",
  "depress", "mdd", "major depressive",
  "adjustment", "panic", "agoraphobia",
  "ocd", "obsessive", "compulsive",
  "bipolar", "schizo", "psychotic",
  "ptsd-related", "mst", "mst-related",
  "insomnia",
];

const MONTHS = [
  "january","february","march","april","may","june",
  "july","august","september","october","november","december",
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function norm(s) {
  return (s || "").trim().toLowerCase().replace(/\s+/g, " ");
}

function isMental(name) {
  const n = norm(name);
  return MENTAL_KEYWORDS.some((k) => n.includes(k));
}

/**
 * Extract a normalised YYYY-MM bucket from a free-text date reference.
 * Supports: "2014-04-15", "04/2014", "April 2014", "Apr 2014".
 * Returns null when no recognisable year can be found.
 */
export function extractYearMonth(input) {
  const s = (input || "").trim().toLowerCase();
  if (!s) return null;
  // ISO or "YYYY-MM" or "YYYY/MM"
  const iso = s.match(/(19\d{2}|20\d{2})[-/](0?[1-9]|1[0-2])/);
  if (iso) return `${iso[1]}-${String(iso[2]).padStart(2, "0")}`;
  // "MM/YYYY" or "M-YYYY"
  const usDate = s.match(/(^|[^\d])(0?[1-9]|1[0-2])[/-](19\d{2}|20\d{2})($|[^\d])/);
  if (usDate) return `${usDate[3]}-${String(usDate[2]).padStart(2, "0")}`;
  // "Month YYYY"
  for (let i = 0; i < MONTHS.length; i++) {
    const m = MONTHS[i];
    const short = m.slice(0, 3);
    const rx = new RegExp(`\\b(${m}|${short})\\.?\\s+(19\\d{2}|20\\d{2})\\b`, "i");
    const match = s.match(rx);
    if (match) return `${match[2]}-${String(i + 1).padStart(2, "0")}`;
  }
  // Just "YYYY" — month-less. Caller decides whether to treat as a match.
  const yr = s.match(/\b(19\d{2}|20\d{2})\b/);
  if (yr) return `${yr[1]}-00`;
  return null;
}

/**
 * Loose name-equivalence — does one condition string "obviously refer to"
 * the same condition as another? Used to spot drift between 526EZ
 * claimedConditions and Claim Tracker issues without false-positiving on
 * substantively different conditions.
 *
 *   "Lumbar strain"  vs "Back pain"            → distinct
 *   "PTSD"           vs "ptsd"                 → same
 *   "Right knee strain" vs "right knee pain"   → same family, returns false
 *                                                 (caller still flags only when one is too generic)
 */
function namesObviouslyEqual(a, b) {
  return norm(a) === norm(b);
}

function add(out, finding) { out.push(finding); }

// ---------------------------------------------------------------------------
// Main entry
// ---------------------------------------------------------------------------

/**
 * @param {Object} args
 * @param {Object} args.master526ez   The Master 526EZ form state.
 * @param {Object} args.form0781      The 21-0781 worksheet state.
 * @param {Array}  args.issues        Claim Tracker issues.
 * @param {Array}  args.statements    User-drafted statements.
 * @param {Object} args.workspace     { id, title, claimType }.
 * @returns {Array} findings (same shape as formAudit.findings).
 */
export function runCrossFormAudit({
  master526ez = {},
  form0781 = {},
  issues = [],
  statements = [],
  workspace = {},
} = {}) {
  const findings = [];
  const claimedConditions = master526ez.claimedConditions || [];

  // -------------------------------------------------------------------------
  // 1. CLAIM-TYPE MISMATCH between workspace and 526EZ
  // -------------------------------------------------------------------------
  const wsType = norm(workspace.claimType);
  const ezType = norm(master526ez.claimType);
  if (wsType && ezType && wsType !== ezType) {
    // Don't flag if one is just a subset of the other ("Original" vs "Original disability compensation claim")
    if (!wsType.includes(ezType) && !ezType.includes(wsType)) {
      add(findings, {
        id: "cf-claimtype-mismatch",
        severity: "confusion",
        title: "Claim type differs between the workspace and the 526EZ",
        why: "VA processes the 526EZ exactly as written. If the workspace and the form disagree on the claim type (e.g. workspace says BDD but the 526EZ says Original), downstream artifacts like the BDD eligibility check, the Master PDF cover sheet, and the audit's BDD-evidence rules will all behave inconsistently.",
        fix: `Pick one canonical claim type. Workspace says "${workspace.claimType}". 526EZ says "${master526ez.claimType}". Update the 526EZ to match, or update the workspace via the title bar.`,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 2. TRACKER ISSUES NOT REPRESENTED ON 526EZ claimedConditions
  //    AND vice versa.
  // -------------------------------------------------------------------------
  const trackerNames = new Set(issues.map((i) => norm(i.claimedAs)).filter(Boolean));
  const ezNames = new Set(claimedConditions.map((c) => norm(c.claimedAs)).filter(Boolean));

  if (issues.length > 0 && claimedConditions.length === 0) {
    add(findings, {
      id: "cf-526ez-empty",
      severity: "critical",
      title: `${issues.length} condition${issues.length === 1 ? "" : "s"} in the Claim Tracker, but the 526EZ has no claimed conditions listed`,
      why: "VA rates only the conditions ACTUALLY LISTED on the 526EZ. Anything in the tracker that's missing from the form will not be adjudicated, regardless of how much evidence you attach.",
      fix: "Add every claimed condition from the Claim Tracker to the 526EZ \"Conditions Being Claimed\" list before filing.",
    });
  } else if (issues.length > 0 && claimedConditions.length > 0) {
    // Tracker → 526EZ drift
    const inTrackerNotEz = issues.filter((i) => i.claimedAs && !ezNames.has(norm(i.claimedAs)));
    if (inTrackerNotEz.length > 0) {
      add(findings, {
        id: "cf-tracker-not-on-526ez",
        severity: "critical",
        title: `${inTrackerNotEz.length} tracker condition${inTrackerNotEz.length === 1 ? " is" : "s are"} missing from the 526EZ`,
        why: "Conditions in the Claim Tracker but NOT on the 526EZ won't be adjudicated by VA. You'll lose credit for any evidence attached to those tracker rows.",
        fix: `Add to the 526EZ: ${inTrackerNotEz.map((i) => `"${i.claimedAs}"`).join(", ")}. Or remove these from the tracker if they're no longer being claimed.`,
      });
    }
    // 526EZ → Tracker drift (signal that names differ — most common reason: "Back pain" on form, "Lumbar strain" in tracker)
    const onEzNotInTracker = claimedConditions.filter((c) => c.claimedAs && !trackerNames.has(norm(c.claimedAs)));
    if (onEzNotInTracker.length > 0) {
      add(findings, {
        id: "cf-526ez-not-in-tracker",
        severity: "confusion",
        title: `${onEzNotInTracker.length} 526EZ condition${onEzNotInTracker.length === 1 ? "" : "s"} doesn't match any tracker condition`,
        why: "Common cause: the 526EZ uses a generic name like \"Back pain\" while the tracker has the precise diagnosis (\"Lumbar strain\"). VA decides on the 526EZ language — and a generic name like \"Back pain\" produces a weaker, more loosely-rated claim than the precise diagnosis.",
        fix: `526EZ entries not found in the tracker: ${onEzNotInTracker.map((c) => `"${c.claimedAs}"`).join(", ")}. Rename the 526EZ entries to match the tracker's precise diagnoses (the tracker uses the language raters expect).`,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 3. MENTAL-HEALTH CLAIMS WITHOUT A 21-0781 (or vice versa)
  // -------------------------------------------------------------------------
  const mentalIssues = issues.filter((i) => i.claimedAs && isMental(i.claimedAs));
  const mhFieldFilled = !!(form0781.mentalHealthConditions || "").trim();
  const form0781InUse = !!(
    (form0781.mentalHealthConditions || "").trim()
    || (form0781.eventDescription || "").trim()
    || (form0781.currentImpact || "").trim()
  );

  if (mentalIssues.length > 0 && !form0781InUse) {
    add(findings, {
      id: "cf-mental-without-0781",
      severity: "critical",
      title: `Mental-health condition${mentalIssues.length === 1 ? "" : "s"} claimed but VA Form 21-0781 is empty`,
      why: "PTSD, MST, and most stressor-based mental-health claims require a 21-0781 to establish the in-service event. Without it the VA either rejects the stressor outright or kicks the claim back for the form.",
      fix: `Open the VA Form 21-0781 panel and complete the stressor narrative for: ${mentalIssues.map((i) => `"${i.claimedAs}"`).join(", ")}.`,
    });
  }

  if (form0781InUse && mentalIssues.length === 0) {
    add(findings, {
      id: "cf-0781-without-mental",
      severity: "review",
      title: "21-0781 worksheet is in use but no mental-health condition is claimed",
      why: "The 21-0781 only supports mental-health stressor claims. Without a mental-health condition in the Claim Tracker or on the 526EZ, the form has nothing to attach to.",
      fix: "Either add the mental-health condition you intend to claim (PTSD, MDD, GAD, adjustment disorder, etc.) to the Claim Tracker, or clear the 21-0781 if it was started in error.",
    });
  }

  // 21-0781 mentions a specific condition that isn't anywhere in the tracker
  if (mhFieldFilled && mentalIssues.length > 0) {
    const mhTokens = norm(form0781.mentalHealthConditions).split(/[,;/]| and /i).map((t) => t.trim()).filter(Boolean);
    const claimedTokens = new Set(mentalIssues.map((i) => norm(i.claimedAs)));
    const orphan = mhTokens.filter((t) => {
      // Only flag tokens long enough to be a real condition name
      if (t.length < 4) return false;
      // Must be a known mental keyword — we don't flag arbitrary words
      if (!isMental(t)) return false;
      // Already represented if any claimed name contains the token
      return ![...claimedTokens].some((c) => c.includes(t) || t.includes(c));
    });
    if (orphan.length > 0) {
      add(findings, {
        id: "cf-0781-condition-not-in-tracker",
        severity: "confusion",
        title: "21-0781 lists a mental-health condition that isn't in the Claim Tracker",
        why: "The 21-0781 establishes the stressor for SPECIFIC mental-health diagnoses. If the form names a condition the tracker doesn't have, VA may rate the stressor against the wrong diagnostic code — or skip rating it entirely.",
        fix: `Add to the Claim Tracker (or remove from the 21-0781): ${orphan.join(", ")}.`,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 4. EVENT-DATE CONTRADICTIONS — 21-0781 eventDate vs statement onset dates
  // -------------------------------------------------------------------------
  const f0781YM = extractYearMonth(form0781.eventDate);
  if (f0781YM && f0781YM !== null) {
    const conflicts = [];
    statements.forEach((stmt) => {
      const answers = stmt.answers || {};
      // Statement onset fields often live under different answer-keys — scan
      // a small allowlist of dateish answer fields rather than every key
      // (which would over-flag).
      const dateAnswers = ["onset", "eventDate", "incidentDate", "stressorDate", "dateOfEvent"];
      dateAnswers.forEach((k) => {
        const v = answers[k];
        if (!v) return;
        const ym = extractYearMonth(typeof v === "string" ? v : "");
        if (!ym) return;
        if (ym !== f0781YM) {
          // YYYY-00 means we only got the year — only flag if YEARS differ
          if (ym.endsWith("-00") || f0781YM.endsWith("-00")) {
            if (ym.slice(0, 4) !== f0781YM.slice(0, 4)) conflicts.push({ stmt, key: k, value: v, ym });
          } else {
            conflicts.push({ stmt, key: k, value: v, ym });
          }
        }
      });
    });
    if (conflicts.length > 0) {
      const lines = conflicts.map((c) => `"${c.stmt.title || c.stmt.type}" → ${c.value}`).join("; ");
      add(findings, {
        id: "cf-event-date-conflict",
        severity: "confusion",
        title: `21-0781 event date doesn't match ${conflicts.length} statement${conflicts.length === 1 ? "" : "s"}`,
        why: "Date contradictions across forms are one of the top reasons VA returns claims for clarification. The 21-0781 date is what the VBMS rater compares against your unit's deployment / event records — a one-month gap to a statement can be enough to weaken the stressor.",
        fix: `21-0781 says ${form0781.eventDate}. Conflicting statements: ${lines}. Pick the correct date and align every form to it.`,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 5. STATEMENT TITLE DOESN'T MAP TO ANY CLAIMED CONDITION
  // -------------------------------------------------------------------------
  if (statements.length > 0 && issues.length > 0) {
    const orphanStatements = statements.filter((s) => {
      const t = norm(s.title);
      if (!t) return false; // untitled is handled by formAudit instead
      // Linked via metadata — explicit issue id wins
      if (s.issueId && issues.find((i) => i.id === s.issueId)) return false;
      // Otherwise check if any claimed condition name is mentioned in the title
      return !issues.some((i) => {
        const n = norm(i.claimedAs);
        return n && (t.includes(n) || n.includes(t));
      });
    });
    if (orphanStatements.length > 0) {
      const titles = orphanStatements.slice(0, 3).map((s) => `"${s.title}"`).join(", ");
      const more = orphanStatements.length > 3 ? ` (+${orphanStatements.length - 3} more)` : "";
      add(findings, {
        id: "cf-orphan-statement",
        severity: "review",
        title: `${orphanStatements.length} statement${orphanStatements.length === 1 ? "" : "s"} not clearly tied to a claimed condition`,
        why: "VA reviewers index statements by condition. A statement that doesn't name (or get linked to) a specific claimed condition can be missed during adjudication — at best it adds noise, at worst the supporting evidence isn't counted.",
        fix: `Either rename the statement to include the condition name, or link it to an issue in the Statement Builder: ${titles}${more}.`,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 6. SECONDARY ISSUE'S PRIMARY POINTER IS DANGLING
  // -------------------------------------------------------------------------
  const issueById = new Map(issues.map((i) => [i.id, i]));
  const dangling = issues.filter((i) => {
    const theory = (i.theory || "").toLowerCase();
    if (!theory.includes("secondary")) return false;
    if (!i.secondaryToIssueId) return false;
    return !issueById.has(i.secondaryToIssueId);
  });
  if (dangling.length > 0) {
    add(findings, {
      id: "cf-secondary-dangling",
      severity: "confusion",
      title: `${dangling.length} secondary claim${dangling.length === 1 ? "" : "s"} point${dangling.length === 1 ? "s" : ""} to a primary that no longer exists`,
      why: "Secondary claims must attach to a real service-connected primary condition. A dangling pointer means the primary was deleted or renamed — VA cannot adjudicate the secondary chain.",
      fix: `Re-select the primary condition for: ${dangling.map((i) => `"${i.claimedAs}"`).join(", ")}. Or switch the theory to Direct / BDD.`,
    });
  }

  // -------------------------------------------------------------------------
  // 7. BDD WORKSPACE BUT SERVICE END IS NOT WITHIN THE BDD WINDOW
  //    BDD window: 180–90 days before separation. Service-end already passed
  //    or > 180 days out → workspace shouldn't be BDD.
  // -------------------------------------------------------------------------
  const isBdd = /bdd|pre[-\s]?discharge|benefits delivery at discharge/i.test(
    workspace.claimType || master526ez.claimType || "",
  );
  if (isBdd && master526ez.serviceEnd) {
    const end = new Date(master526ez.serviceEnd);
    if (!Number.isNaN(end.getTime())) {
      const now = new Date();
      const daysUntilSep = Math.floor((end - now) / (1000 * 60 * 60 * 24));
      if (daysUntilSep > 180) {
        add(findings, {
          id: "cf-bdd-too-early",
          severity: "review",
          title: `Workspace is BDD but separation is more than 180 days away (${daysUntilSep} days)`,
          why: "The BDD program requires service-end within 90–180 days of filing. Filing earlier than 180 days out routes the claim to the standard track, not BDD.",
          fix: "Either wait until you're inside the 180-day window or switch the workspace claim type to Original / Standard.",
        });
      } else if (daysUntilSep < 0) {
        add(findings, {
          id: "cf-bdd-too-late",
          severity: "confusion",
          title: "Workspace is BDD but separation date has already passed",
          why: "BDD only applies BEFORE separation. After discharge the claim must be filed as Original, FDC, or a post-service variant — using the BDD track now will cause routing errors.",
          fix: "Switch the workspace claim type to Original or FDC.",
        });
      }
    }
  }

  // -------------------------------------------------------------------------
  // 8. ESTIMATED RATING SUM > 100% — flag impossible combined math
  // -------------------------------------------------------------------------
  const ratingSum = issues.reduce((acc, i) => acc + (Number(i.estimatedRating) || 0), 0);
  if (issues.length > 0 && ratingSum > 100) {
    // This is a "VA-math hygiene" flag — raw sum >100% is fine for individual
    // ratings combining to <100, but it's a sign the user may be confusing
    // additive math with VA combined-rating math.
    add(findings, {
      id: "cf-rating-sum-warning",
      severity: "review",
      title: `Individual estimated ratings add to ${ratingSum}% — remember VA uses combined math`,
      why: "VA does NOT add ratings linearly. Per 38 CFR § 4.25, each additional rating only reduces the remaining efficiency, then the total is rounded to the nearest 10. Two 50% conditions combine to 75%, not 100%.",
      fix: "Check the VA Math page for the correctly-combined projection. Adjust per-condition estimates so the combined total reflects realistic functional impairment.",
    });
  }

  return findings;
}
